import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FiCheckCircle, FiCreditCard, FiList, FiXCircle } from "react-icons/fi";
import Nav from "../../components/home/Nav";
import { useDispatch, useSelector } from "react-redux";
import { orderDetailUrl } from "../../utils/orderUrl";
import {
  usePaymentSettingsQuery,
  useReconcilePaymentMutation,
  useRequestPaymentMutation,
  useRequestSnappPaymentMutation
} from "../../store/services/paymentService";
import { useGetOrderDetailsQuery } from "../../store/services/userOrdersService";
import { useFeedback } from "../../context/AppFeedbackContext";
import { emptyCart } from "../../store/reducers/cartReducer";
import { clearPendingPayment } from "../../utils/pendingPayment";
import { trackAnalytics } from "../../utils/analytics";
import { trackPaymentTelemetry, goToTrackedPaymentUrl } from "../../utils/paymentTelemetry";
import {
  canAutoRedirectPayment,
  copyPaymentUrl,
  getPaymentBrowserInfo,
  getPaymentRedirectCopy,
  getPaymentUrl
} from "../../utils/paymentRedirect";
import "../../styles/payment-redirect.css";

const PaymentResult = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useFeedback();
  const { userToken } = useSelector((state) => state.authReducer);

  const orderId = params.get("order_ref") || params.get("order_id");
  const refId = params.get("ref_id");
  const requestedStatus = params.get("status");

  const [requestPayment, paymentResponse] = useRequestPaymentMutation();
  const [requestSnappPayment, snappPaymentResponse] = useRequestSnappPaymentMutation();
  const [reconcilePayment] = useReconcilePaymentMutation();
  const paymentSettingsResponse = usePaymentSettingsQuery();
  const snappPayEnabled = Boolean(paymentSettingsResponse.data?.snapp_pay?.enabled);
  const [retryRedirect, setRetryRedirect] = useState(null);
  const [retryError, setRetryError] = useState("");
  const [copyPaymentMessage, setCopyPaymentMessage] = useState("");

  const orderCheck = useGetOrderDetailsQuery(orderId, {
    skip: !userToken || !orderId
  });

  const verifiedOrder = orderCheck.data?.order;
  const paymentStatus = verifiedOrder?.payment_status || "";
  const isPaid = paymentStatus === "paid";
  const isPending =
    paymentStatus === "pending" ||
    (!verifiedOrder && requestedStatus === "pending");
  const isLoading = Boolean(orderId && userToken && !verifiedOrder && orderCheck.isLoading);
  const resultState = isPaid ? "paid" : (isPending || isLoading ? "pending" : "failed");
  const retryMethod =
    verifiedOrder?.payment_method === "snapp_pay" && snappPayEnabled
      ? "snapp_pay"
      : "online_gateway";
  const canRetryPayment = Boolean(userToken && verifiedOrder && resultState === "failed");
  const retryLoading =
    paymentResponse.isLoading ||
    snappPaymentResponse.isLoading ||
    Boolean(retryRedirect);

  useEffect(() => {
    if (!isPaid) {
      return;
    }

    dispatch(emptyCart());
    clearPendingPayment(orderId);
  }, [dispatch, isPaid, orderId]);

  useEffect(() => {
    if (!orderId || !userToken) {
      return;
    }

    if (Number(orderCheck.error?.status) === 404) {
      clearPendingPayment(orderId);
      navigate("/cart", { replace: true });
    }
  }, [navigate, orderCheck.error, orderId, userToken]);

  useEffect(() => {
    if (
      !orderId ||
      !userToken ||
      !verifiedOrder ||
      resultState !== "pending" ||
      verifiedOrder.payment_method === "snapp_pay"
    ) {
      return;
    }

    let cancelled = false;
    let timeoutId = null;
    let attempts = 0;

    const reconcile = async () => {
      if (cancelled || attempts >= 8) {
        return;
      }

      attempts += 1;

      try {
        await reconcilePayment(orderId).unwrap();
        await orderCheck.refetch();
      } catch {}

      if (!cancelled && attempts < 8) {
        timeoutId = window.setTimeout(reconcile, 3500);
      }
    };

    reconcile();

    return () => {
      cancelled = true;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [orderCheck, orderId, reconcilePayment, resultState, userToken, verifiedOrder]);

  useEffect(() => {
    if (!orderId) {
      return;
    }

    trackAnalytics("payment_result_loaded", {
      metadata: {
        order_ref: orderId,
        ref_id: refId || "",
        state: resultState,
        source: "payment_result"
      }
    });
  }, [orderId, refId, resultState]);

  const startRetryRedirect = (paymentUrl, context = {}) => {
    const autoRedirect = canAutoRedirectPayment();
    const browser = getPaymentBrowserInfo();
    const meta = {
      ...context,
      payment_method: retryMethod,
      auto_redirect: autoRedirect,
      browser
    };

    setRetryRedirect({
      ...getPaymentRedirectCopy(retryMethod),
      url: paymentUrl,
      autoRedirect,
      context: meta
    });

    trackPaymentTelemetry("payment_redirect_screen_shown", {
      metadata: {
        ...meta,
        source: "payment_retry"
      }
    });

    if (autoRedirect) {
      window.setTimeout(() => {
        goToTrackedPaymentUrl(paymentUrl, {
          ...meta,
          source: "payment_retry",
          trigger: "auto"
        });
      }, browser.embedded ? 550 : 350);
    }
  };

  const copyCurrentPaymentUrl = async () => {
    if (!retryRedirect?.url) {
      return;
    }

    const copied = await copyPaymentUrl(retryRedirect.url);
    setCopyPaymentMessage(copied ? "لینک پرداخت کپی شد." : "کپی لینک انجام نشد.");
  };

  const retryPayment = async () => {
    if (!orderId || retryLoading || !canRetryPayment) {
      return;
    }

    setRetryError("");
    setCopyPaymentMessage("");
    setRetryRedirect(null);

    try {
      const payment = retryMethod === "snapp_pay"
        ? await requestSnappPayment(orderId).unwrap()
        : await requestPayment(orderId).unwrap();

      const paymentUrl = getPaymentUrl(payment);

      if (!paymentUrl) {
        throw new Error("payment_url_missing");
      }

      startRetryRedirect(paymentUrl, {
        order_ref: orderId,
        attempt_id: payment?.attempt_id || "",
        attempt_uuid: payment?.attempt_uuid || ""
      });
    } catch (error) {
      const message = !navigator.onLine || error?.status === "FETCH_ERROR"
        ? "ارتباط اینترنت برقرار نیست. اتصال را بررسی کنید و دوباره تلاش کنید."
        : error?.data?.message || "امکان ادامه پرداخت وجود ندارد.";

      setRetryError(message);
      notify(message, "warning", "پرداخت");
    }
  };

  const title =
    resultState === "paid"
      ? "پرداخت انجام شد"
      : resultState === "pending"
        ? "در حال بررسی پرداخت"
        : "پرداخت انجام نشد";

  const description =
    resultState === "paid"
      ? "سفارش شما با موفقیت ثبت شد."
      : resultState === "pending"
        ? verifiedOrder?.payment_method === "snapp_pay"
          ? "نتیجه پرداخت قبلی در حال بررسی است. برای جلوگیری از پرداخت تکراری، پرداخت جدیدی ایجاد نمی‌شود."
          : "لطفاً چند لحظه صبر کنید."
        : "می‌توانید دوباره پرداخت را ادامه دهید.";

  const statusLabel =
    resultState === "paid"
      ? "پرداخت شده"
      : resultState === "pending"
        ? "در حال بررسی"
        : "انجام نشد";

  return (
    <>
      <Nav />

      {retryRedirect && (
        <div className="payment-redirect-overlay" dir="rtl">
          <div className="payment-redirect-card">
            <span className="payment-redirect-spinner"></span>
            <strong>{retryRedirect.title || "انتقال به پرداخت"}</strong>
            <p>{retryRedirect.message || "در حال آماده‌سازی صفحه پرداخت..."}</p>

            <div className="payment-redirect-actions">
              <button
                type="button"
                className="payment-redirect-button"
                onClick={() =>
                  goToTrackedPaymentUrl(retryRedirect.url, {
                    ...(retryRedirect.context || {}),
                    source: "payment_retry",
                    trigger: "manual"
                  })
                }
              >
                ادامه پرداخت
              </button>

              {!retryRedirect.autoRedirect && (
                <button
                  type="button"
                  className="payment-redirect-link-button"
                  onClick={copyCurrentPaymentUrl}
                >
                  کپی لینک پرداخت
                </button>
              )}
            </div>

            {copyPaymentMessage && <small>{copyPaymentMessage}</small>}
          </div>
        </div>
      )}

      <main className={`payment-result-page ${resultState === "paid" ? "payment-result-success" : "payment-result-failed"}`}>
        <div className="site-container">
          <section className="payment-result-card">
            <div className="payment-result-mark">
              {resultState === "paid" ? (
                <FiCheckCircle size={42} />
              ) : resultState === "pending" ? (
                <span className="payment-redirect-spinner"></span>
              ) : (
                <FiXCircle size={42} />
              )}
            </div>

            <span className="payment-result-kicker">
              {statusLabel}
            </span>

            <h1>{title}</h1>
            <p>{description}</p>

            {verifiedOrder && (
              <div className="payment-result-info">
                <div>
                  <span>شماره سفارش</span>
                  <strong>{verifiedOrder.order_number || "-"}</strong>
                </div>

                <div>
                  <span>وضعیت</span>
                  <strong>{statusLabel}</strong>
                </div>

                {resultState === "paid" && (
                  <div>
                    <span>کد پیگیری</span>
                    <strong>{refId || verifiedOrder.payment_ref_id || "-"}</strong>
                  </div>
                )}
              </div>
            )}

            {retryError && <p className="payment-result-error">{retryError}</p>}

            <div className="payment-result-actions">
              {canRetryPayment && (
                <button
                  type="button"
                  className="payment-result-primary"
                  onClick={retryPayment}
                  disabled={retryLoading}
                >
                  <FiCreditCard size={18} />
                  {retryLoading ? "در حال اتصال..." : "پرداخت مجدد"}
                </button>
              )}

              {resultState === "paid" && verifiedOrder && (
                <Link className="payment-result-primary" to={orderDetailUrl(orderId)}>
                  <FiList size={18} />
                  جزئیات سفارش
                </Link>
              )}

              {resultState !== "paid" && (
                <Link to="/cart">
                  بازگشت به سبد
                </Link>
              )}

              {resultState === "paid" && (
                <Link to="/orders">
                  سفارش‌ها
                </Link>
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default PaymentResult;
